import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { isValid, parse } from "date-fns";
import {
  AsteriskIcon,
  CalendarIcon,
  CircleAlertIcon,
  CopyIcon,
  EyeIcon,
  InfoIcon,
  PlusIcon,
  SaveIcon,
  SendIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import uuid from "uuid";

import { formatDate, formatDateAt } from "simplydo/core/dates";
import { removeLocalStorageItem } from "simplydo/core/helpers";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import {
  Checkbox,
  CheckboxHeader,
  CheckboxContent,
} from "simplydo/react/ui/checkbox";
import { DateInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import util from "utils/utils";

import {
  type ConditionCheckFieldType,
  availableConditions,
  conditionChecks,
  getDefaultCondition,
  renderValueCheck,
} from "components/ideas/Body/Custom/fieldCondition";

import AnnouncementBase from "./Base";
import type {
  AnnouncementConditionConfig,
  AnnouncementIdeaFieldCondition,
  AnnouncementListSetState,
  AnnouncementRecipientCondition,
  AnnouncementSendWhenCondition,
  AnnouncementSetState,
  AnnouncementSubconditionOptions,
  EditableAnnouncement,
} from "./types";
import {
  EMPTY_ANNOUNCEMENT,
  dropdownStringValue,
  isAnnouncementClosed,
  isSendAtValid,
} from "./types";

type IdeaTemplateField = NonNullable<
  OpenAPI.Schemas["IdeaTemplate"]["body"]
>[number]["fields"][number];

type AnnouncementModalProps = {
  forType: string;
  forId: string;
  standardConditions: AnnouncementConditionConfig[];
  automaticConditions?: AnnouncementConditionConfig[];
  standardConditionsRequired: boolean;
  scheduledAnnouncements: EditableAnnouncement[];
  announcement: EditableAnnouncement;
  deleteAnnouncement: (id: string, callback: () => void) => void;
  setAnnouncement: AnnouncementSetState;
  setScheduledAnnouncements: AnnouncementListSetState;
  automaticAnnouncements: EditableAnnouncement[];
  setAutomaticAnnouncements: AnnouncementListSetState;
  copyToNew?: (announcement: EditableAnnouncement) => void;
  enabledAnnouncementTypes?: string[];
  setEmailPreviewOpen: (open: boolean) => void;
};

const AnnouncementModal = ({
  forType,
  forId,
  standardConditions,
  standardConditionsRequired,
  scheduledAnnouncements,
  setScheduledAnnouncements,
  announcement,
  setAnnouncement,
  deleteAnnouncement,
  automaticConditions,
  setAutomaticAnnouncements,
  automaticAnnouncements,
  copyToNew,
  setEmailPreviewOpen,
  enabledAnnouncementTypes = ["standard", "automatic"],
}: AnnouncementModalProps) => {
  const [recipientsMatched, setRecipientsMatched] = useState(0);
  const [subconditionOptions, setSubconditionOptions] =
    useState<AnnouncementSubconditionOptions>({});
  const [sending, setSending] = useState(false);
  const { t } = useTranslation();

  const addCondition = useCallback(() => {
    setAnnouncement((prevAnn) => ({
      ...prevAnn,
      recipientConditions: [
        ...(prevAnn.recipientConditions ?? []),
        { type: "", data: "", key: uuid.v4() },
      ],
    }));
  }, [setAnnouncement]);

  const getStandardSubconditions = useCallback(
    (standardCondition: AnnouncementRecipientCondition | undefined) =>
      standardConditions.find((sc) => sc.value === standardCondition?.type)
        ?.subConditions,
    [standardConditions],
  );
  const getAutomaticSubconditions = useCallback(
    (condition: string | undefined) =>
      automaticConditions?.find((sc) => sc.value === condition)?.subConditions,
    [automaticConditions],
  );
  const automaticSubconditions = getAutomaticSubconditions(
    announcement.sendWhen,
  );

  const updateCondition = useCallback(
    (
      conditionIndex: number,
      label: keyof AnnouncementRecipientCondition,
      value: AnnouncementRecipientCondition[keyof AnnouncementRecipientCondition],
    ) => {
      const updatedRecipientConditions = [
        ...(announcement.recipientConditions ?? []),
      ];
      const condition = updatedRecipientConditions[conditionIndex];
      if (!condition) return;
      updatedRecipientConditions[conditionIndex] = {
        ...condition,
        [label]: value,
        ...(label === "type" ? { data: "" } : {}),
      };
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  const deleteCondition = useCallback(
    (conditionIndex: number) => {
      const updatedRecipientConditions = [
        ...(announcement.recipientConditions ?? []),
      ];
      updatedRecipientConditions.splice(conditionIndex, 1);
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  const updateSubconditionOptions = useCallback(
    (
      conditionKey: string | undefined,
      option: string | string[],
      selectSingle: boolean,
    ) => {
      if (!conditionKey) return;
      const updatedRecipientConditions = (
        announcement.recipientConditions ?? []
      ).map((condition) => {
        if (condition.key !== conditionKey) {
          return condition;
        }
        if (selectSingle) {
          return { ...condition, data: option };
        }

        const options = Array.isArray(option) ? option : [option];
        return { ...condition, data: options };
      });
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  useEffect(() => {
    let fetching = true;
    api.announcements.getAudience(
      forType,
      forId,
      {
        type: "standard",
        recipientConditions: announcement.recipientConditions,
      },
      ({ recipientsMatched: response }) => {
        if (!fetching) return;
        setRecipientsMatched(response);
      },
      (err) => {
        if (!fetching) return;
        toast.error(err.message);
        setRecipientsMatched(0);
      },
    );

    return () => {
      fetching = false;
    };
  }, [forType, forId, announcement.recipientConditions]);

  const createAnnouncement = useCallback(() => {
    if (announcement.replyTo && !util.validateEmail(announcement.replyTo)) {
      toast.error("Please enter a valid reply-to email address");
      return;
    }

    setSending(true);

    const resolvedType =
      announcement.type === "standard" && announcement.sendAt
        ? "scheduled"
        : announcement.type;

    const {
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      recipientConditions,
      calendarEvent,
      attachments,
      sendAt,
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
      teamsMeeting,
    } = announcement;

    const type = resolvedType;

    const automaticFields = {
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
    };
    const scheduledFields = { recipientConditions, sendAt };

    const standardFields = { recipientConditions };
    api.announcements.create(
      forType,
      forId,
      {
        type,
        subject,
        body,
        text,
        replyTo,
        callToActionText,
        callToActionLink,
        calendarEvent,
        attachments,
        teamsMeeting,
        ...(type === "automatic" ? automaticFields : {}),
        ...(type === "scheduled" ? scheduledFields : {}),
        ...(type === "standard" ? standardFields : {}),
      },
      (createdAnnouncement) => {
        const savedAnnouncement = createdAnnouncement as EditableAnnouncement;
        toast.success(
          savedAnnouncement.type === "standard"
            ? t("announcements.sent", {
                audience: `${savedAnnouncement.recipientsMatched ?? 0} recipients`,
              })
            : t("announcements.created"),
        );
        setSending(false);
        setAnnouncement(EMPTY_ANNOUNCEMENT);
        setRecipientsMatched(0);
        removeLocalStorageItem(`announcement-${forId}`);
        if (savedAnnouncement.type === "automatic") {
          setAutomaticAnnouncements((prev) => [...prev, savedAnnouncement]);
        } else if (savedAnnouncement.type === "scheduled") {
          setScheduledAnnouncements((prev) => [...prev, savedAnnouncement]);
        }
        setTimeout(() => {
          api.invalidateQueries(api.announcements.getLogs, [forType, forId]);
        }, 1000);
      },
      (err) => {
        setSending(false);
        toast.error(err.message);
      },
    );
  }, [
    forType,
    forId,
    announcement,
    setAnnouncement,
    setScheduledAnnouncements,
    setAutomaticAnnouncements,
    t,
  ]);

  const updateAnnouncement = useCallback(() => {
    if (announcement.replyTo && !util.validateEmail(announcement.replyTo)) {
      toast.error("Please enter a valid reply-to email address");
      return;
    }

    setSending(true);
    const {
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      recipientConditions,
      calendarEvent,
      attachments,
      type,
      sendAt,
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
      teamsMeeting,
    } = announcement;

    const automaticFields = {
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
    };
    const standardFields = { recipientConditions };
    const scheduledFields = { recipientConditions, sendAt };
    api.announcements.update(
      forType,
      forId,
      announcement._id ?? "",
      {
        type,
        subject,
        body,
        text,
        sendWhen,
        replyTo,
        callToActionText,
        callToActionLink,
        calendarEvent,
        attachments,
        teamsMeeting,
        ...(type === "automatic" ? automaticFields : {}),
        ...(type === "scheduled" ? scheduledFields : {}),
        ...(type === "standard" ? standardFields : {}),
      },
      (updatedAnnouncement) => {
        const savedAnnouncement = updatedAnnouncement as EditableAnnouncement;
        if (savedAnnouncement.type === "automatic") {
          setAutomaticAnnouncements(
            automaticAnnouncements.map((a) =>
              a._id === savedAnnouncement._id ? savedAnnouncement : a,
            ),
          );
        } else {
          setScheduledAnnouncements(
            scheduledAnnouncements.map((a) =>
              a._id === savedAnnouncement._id ? savedAnnouncement : a,
            ),
          );
        }
        // Need to invalidate board lane query to reflect changes to subject in the move-to-lane announcement type
        if (forType === "challenge" && announcement.type === "automatic") {
          api.invalidateQueries(api.boards.getProjectLanes, [forId]);
        }
        setSending(false);
        setAnnouncement(EMPTY_ANNOUNCEMENT);
        removeLocalStorageItem(`announcement-${forId}`);
        toast.success(t("generic.typeSaved", { type: "Announcement" }));
      },
      (err) => {
        toast.error(err.message);
        setSending(false);
      },
    );
  }, [
    t,
    forType,
    forId,
    announcement,
    scheduledAnnouncements,
    setScheduledAnnouncements,
    setAnnouncement,
    automaticAnnouncements,
    setAutomaticAnnouncements,
  ]);

  const { recipientConditions = [] } = announcement;

  useEffect(() => {
    if (announcement.type === "automatic") {
      const cond = (automaticConditions ?? []).find(
        (sc) => sc.value === announcement.sendWhen,
      );
      if (cond?.subConditions) {
        cond.subConditions.forEach((subcondition) => {
          if (
            subcondition.getOptions &&
            subcondition.key &&
            !subconditionOptions[subcondition.key]
          ) {
            subcondition.getOptions(forId, (options) =>
              setSubconditionOptions((prev) => ({
                ...prev,
                [subcondition.key]: options,
              })),
            );
          }
        });
      }
    } else {
      recipientConditions.forEach((c) => {
        const cond = standardConditions.find((sc) => sc.value === c.type);
        if (cond?.subConditions) {
          cond.subConditions.forEach((subcondition) => {
            if (
              subcondition.getOptions &&
              subcondition.key &&
              !subconditionOptions[subcondition.key]
            ) {
              subcondition.getOptions(forId, (options) =>
                setSubconditionOptions((prev) => ({
                  ...prev,
                  [subcondition.key]: options,
                })),
              );
            }
          });
        }
      });
    }
  }, [
    standardConditions,
    subconditionOptions,
    forId,
    recipientConditions,
    announcement.type,
    announcement.sendWhen,
    automaticConditions,
  ]);

  const [sendDisabled, sendDisabledReason] = useMemo(() => {
    const hasSendAt =
      announcement.sendAt !== undefined &&
      announcement.sendAt !== null &&
      announcement.sendAt !== "";

    if (util.isEmpty(announcement.subject))
      return [true, "Please fill in the announcement subject and body."];
    if (util.isEmpty(announcement.body))
      return [true, "Please fill in the announcement subject and body."];
    if (announcement.type === "automatic") {
      if (!announcement.sendWhen) {
        return [true, "At least one automation rule is required."];
      }
    } else {
      if (
        standardConditionsRequired &&
        standardConditions?.length &&
        recipientConditions.length === 0
      ) {
        return [true, "Announcements must be sent to at least one user."];
      }
      if (
        (announcement.type === "scheduled" || hasSendAt) &&
        announcement.sendAt &&
        !isValid(new Date(announcement.sendAt))
      ) {
        return [true, "Please select a valid send date and time."];
      }
    }
    if (announcement.calendarEvent) {
      if (
        announcement.calendarEvent.startDate &&
        !isValid(new Date(announcement.calendarEvent.startDate))
      ) {
        return [
          true,
          "Please select a valid calendar event start date and time.",
        ];
      }
      if (
        announcement.calendarEvent.endDate &&
        !isValid(new Date(announcement.calendarEvent.endDate))
      ) {
        return [
          true,
          "Please select a valid calendar event end date and time.",
        ];
      }
      if (!announcement.calendarEvent.organiserName) {
        return [true, "Please enter a calendar event organiser name."];
      }
      if (!announcement.calendarEvent.organiserEmail) {
        return [true, "Please enter a calendar event organiser email."];
      }
      if (!announcement.calendarEvent.name) {
        return [true, "Please enter a calendar event name."];
      }
    }

    return [false, null];
  }, [
    announcement,
    standardConditions,
    recipientConditions,
    standardConditionsRequired,
  ]);

  const sendAtValid = useMemo(
    () => isSendAtValid(announcement.sendAt),
    [announcement.sendAt],
  );

  const mapSubconditionOptions = (
    options: AnnouncementSubconditionOptions[string] | undefined,
  ) =>
    (options ?? []).map((s) => ({
      key: s._id ?? s.value ?? s.key ?? "",
      value: s._id ?? s.value ?? s.key ?? "",
      text: s.name ?? s.text ?? s.title ?? "",
    }));
  return (
    <Modal
      open={!isAnnouncementClosed(announcement)}
      onClose={() => setAnnouncement(EMPTY_ANNOUNCEMENT)}
    >
      <ModalHeader>
        {announcement._id ? (
          t("announcements.automatic.edit.title")
        ) : (
          <>
            {localStorage.getItem(`announcement-${forId}`)
              ? t("announcements.manual.continue.title")
              : t("announcements.automatic.new.title")}
            <br />
            <small>{t("announcements.manual.sendInfo")}</small>
          </>
        )}
      </ModalHeader>
      <ModalContent scrolling>
        <Card fluid>
          <CardContent>
            <H3>Announcement type</H3>
            {(announcement._id &&
              (announcement.type === "standard" ||
                announcement.type === "scheduled")) ||
            !announcement._id ? (
              <>
                <Checkbox
                  type="radio"
                  name="announcementType"
                  value="standard"
                  checked={
                    announcement.type === "standard" ||
                    announcement.type === "scheduled"
                  }
                  onChange={() =>
                    setAnnouncement((prev) => ({ ...prev, type: "standard" }))
                  }
                >
                  <CheckboxHeader>Standard</CheckboxHeader>
                  <CheckboxContent>
                    Send or schedule announcements to a custom audience
                  </CheckboxContent>
                </Checkbox>
                {(announcement.type === "standard" ||
                  announcement.type === "scheduled") && (
                  <div
                    style={{
                      marginLeft: 30,
                      marginBottom: 20,
                      borderLeft: "1px solid #d3cfcf",
                      paddingLeft: 20,
                    }}
                  >
                    {standardConditions?.length ? (
                      <>
                        <H5>
                          {t("announcements.manual.recipients.title")}
                          <Subheader>
                            {t("announcements.manual.recipients.info")}
                          </Subheader>
                        </H5>
                        {standardConditionsRequired ? null : (
                          <p>
                            {t(
                              "announcements.manual.recipients.condition.optional",
                              { type: forType },
                            )}
                          </p>
                        )}
                        <H5>
                          Recipient conditions{" "}
                          {standardConditionsRequired ? (
                            <Icon
                              icon={AsteriskIcon}
                              style={{
                                position: "relative",
                                top: -5,
                                marginLeft: 3,
                              }}
                              color="red"
                              size="small"
                            />
                          ) : null}
                        </H5>
                        {recipientConditions.map((c, i) => {
                          const subconditions = getStandardSubconditions(c);

                          return (
                            <>
                              <Grid mobile={1} columns={16} key={c.key}>
                                <GridColumn span={16} computer={8}>
                                  <Dropdown
                                    variant="outline"
                                    placeholder={t(
                                      "announcements.manual.recipients.condition.select",
                                    )}
                                    fluid
                                    options={standardConditions.map((s) => ({
                                      ...s,
                                      key: s.value,
                                      text: t(s.text),
                                    }))}
                                    onChange={(_e, s) => {
                                      updateCondition(
                                        i,
                                        "type",
                                        dropdownStringValue(s.value) ?? null,
                                      );
                                    }}
                                    value={c.type ?? undefined}
                                  />
                                </GridColumn>
                                <GridColumn span={16} computer={6}>
                                  {subconditions?.map((subcondition) => {
                                    return (
                                      <>
                                        {subcondition.type === "dropdown" ? (
                                          <Dropdown
                                            variant="outline"
                                            multiple={
                                              subcondition.multiple ?? false
                                            }
                                            placeholder={
                                              subcondition.placeholder ??
                                              "Select an option"
                                            }
                                            fluid
                                            options={(
                                              subconditionOptions[
                                                subcondition.key
                                              ] ?? []
                                            ).map((s) => ({
                                              key: s._id,
                                              value: s._id,
                                              text: s.name,
                                            }))}
                                            onChange={(_e, s) => {
                                              updateSubconditionOptions(
                                                c.key ?? "",
                                                s.value as string | string[],
                                                !subcondition.multiple,
                                              );
                                            }}
                                            value={
                                              c.data === null
                                                ? undefined
                                                : c.data
                                            }
                                          />
                                        ) : null}
                                        {subcondition.type === "date"
                                          ? (() => {
                                              const dateValue =
                                                typeof c.data === "string"
                                                  ? c.data
                                                  : "";
                                              return (
                                                <DateInput
                                                  placeholder="Date"
                                                  value={
                                                    dateValue
                                                      ? formatDate(
                                                          dateValue,
                                                          "yyyy-MM-dd",
                                                        )
                                                      : ""
                                                  }
                                                  dateFormat="yyyy-MM-dd"
                                                  iconPosition="left"
                                                  closable
                                                  onChange={(_e, { value }) => {
                                                    const parsed = parse(
                                                      value as string,
                                                      "yyyy-MM-dd",
                                                      new Date(),
                                                    );
                                                    updateSubconditionOptions(
                                                      c.key ?? "",
                                                      isValid(parsed)
                                                        ? parsed.toISOString()
                                                        : "",
                                                      true,
                                                    );
                                                  }}
                                                />
                                              );
                                            })()
                                          : null}
                                      </>
                                    );
                                  })}
                                </GridColumn>
                                <GridColumn
                                  span={16}
                                  computer={2}
                                  style={{ marginTop: 5, textAlign: "center" }}
                                >
                                  <Button
                                    size="small"
                                    onClick={() => deleteCondition(i)}
                                    variant="outline"
                                  >
                                    <Icon icon={Trash2Icon} />
                                  </Button>
                                </GridColumn>
                              </Grid>
                              {i !== recipientConditions.length - 1 ? (
                                <Divider horizontal>And</Divider>
                              ) : null}
                            </>
                          );
                        })}
                        <Button
                          size="small"
                          onClick={addCondition}
                          style={{ marginTop: 10 }}
                          variant="outline"
                        >
                          <Icon icon={PlusIcon} />
                          {t("announcements.manual.recipients.condition.add")}
                        </Button>
                        {recipientsMatched === 0 ? (
                          <H4 style={{ marginTop: 20 }}>
                            <Icon icon={TriangleAlertIcon} />{" "}
                            {t("announcements.manual.recipients.none")}
                          </H4>
                        ) : (
                          <H4 style={{ marginTop: 20 }}>
                            This announcement will be sent to{" "}
                            {recipientsMatched} users.
                          </H4>
                        )}
                      </>
                    ) : (
                      <H3>{t("announcements.manual.recipients.all")}</H3>
                    )}
                  </div>
                )}
              </>
            ) : null}
            {enabledAnnouncementTypes.includes("automatic") &&
              (announcement.type === "automatic" || !announcement._id) && (
                <div>
                  <Checkbox
                    type="radio"
                    name="announcementType"
                    value="automatic"
                    checked={announcement.type === "automatic"}
                    onChange={() =>
                      setAnnouncement((prev) => ({
                        ...prev,
                        type: "automatic",
                      }))
                    }
                  >
                    <CheckboxHeader>Automatic</CheckboxHeader>
                    <CheckboxContent>
                      Configure announcements to be sent automatically based on
                      user actions
                    </CheckboxContent>
                  </Checkbox>
                  {announcement.type === "automatic" && (
                    <div
                      style={{
                        marginLeft: 30,
                        borderLeft: "1px solid #d3cfcf",
                        paddingLeft: 20,
                      }}
                    >
                      <H5>
                        Automation rule
                        <Subheader>
                          Please choose which condition must be met to send this
                          announcement.
                        </Subheader>
                      </H5>
                      <>
                        <FormField required>
                          <FormLabel>
                            {t("announcements.automatic.list.sendWhen")}
                          </FormLabel>
                          <Dropdown
                            variant="outline"
                            placeholder="Select an event trigger"
                            options={(automaticConditions ?? []).map((c) => ({
                              key: c.value,
                              text: t(c.text),
                              value: c.value,
                            }))}
                            value={announcement.sendWhen}
                            onChange={(_e, { value }) => {
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                sendWhen: dropdownStringValue(value),
                              }));
                            }}
                          />
                        </FormField>
                        {announcement.sendWhen ===
                        "submitIdeaWithConditions" ? (
                          <>
                            <p>
                              Only send this announcement if{" "}
                              <strong>
                                <Dropdown
                                  value={
                                    announcement.sendWhenConditions
                                      ?.conditionType ?? "any"
                                  }
                                  options={[
                                    {
                                      key: "any",
                                      text: "any of",
                                      value: "any",
                                    },
                                    {
                                      key: "all",
                                      text: "all of",
                                      value: "all",
                                    },
                                  ]}
                                  onChange={(_e, { value }) => {
                                    const conditionType =
                                      dropdownStringValue(value);
                                    setAnnouncement((prevState) => ({
                                      ...prevState,
                                      sendWhenConditions: {
                                        ...(prevState.sendWhenConditions || {}),
                                        conditionType:
                                          conditionType === "all"
                                            ? "all"
                                            : "any",
                                      },
                                    }));
                                  }}
                                />
                              </strong>{" "}
                              the following conditions are met:
                            </p>
                            {announcement.sendWhenConditions?.conditions?.map(
                              (condition, i) => {
                                const ideaCondition =
                                  condition as AnnouncementIdeaFieldCondition;
                                const chosenFieldType =
                                  subconditionOptions.ideaFields?.find(
                                    (f) => f.id === ideaCondition.field,
                                  )?.type;
                                const isKnownFieldType = (
                                  fieldType: string | undefined,
                                ): fieldType is ConditionCheckFieldType =>
                                  !!fieldType && fieldType in conditionChecks;
                                const selectedIdeaField =
                                  (subconditionOptions.ideaFields?.find(
                                    (chosenField) =>
                                      chosenField.id === ideaCondition.field,
                                  ) as IdeaTemplateField | undefined) ??
                                  ({
                                    id: ideaCondition.field ?? "",
                                    type: "shorttext",
                                  } as IdeaTemplateField);

                                return (
                                  <>
                                    <Grid
                                      columns={4}
                                      mobile={1}
                                      key={`${i}-${ideaCondition.field ?? ""}-${ideaCondition.condition ?? ""}`}
                                      style={{ alignItems: "end" }}
                                    >
                                      <GridColumn>
                                        <FormField>
                                          {i === 0 ? (
                                            <FormLabel
                                              htmlFor={`condition-${i}`}
                                            >
                                              1. Field with name
                                              {ideaCondition.field &&
                                                !subconditionOptions.ideaFields
                                                  ?.filter(
                                                    (f) =>
                                                      !!f.type &&
                                                      f.type in conditionChecks,
                                                  )
                                                  ?.map((f) => f.id)
                                                  .includes(
                                                    ideaCondition.field ?? "",
                                                  ) && (
                                                  <Popup
                                                    trigger={
                                                      <Icon
                                                        icon={CircleAlertIcon}
                                                        color="red"
                                                        style={{
                                                          marginLeft: 5,
                                                        }}
                                                      />
                                                    }
                                                  >{`This announcement condition is invalid because the specified field is not present in the ${t("generic.challenge")} ${t("generic.idea")} format.`}</Popup>
                                                )}
                                            </FormLabel>
                                          ) : null}
                                          <Dropdown
                                            placeholder={
                                              automaticSubconditions?.[0]
                                                ?.placeholder
                                            }
                                            clearable
                                            variant="outline"
                                            value={
                                              ideaCondition.field ?? undefined
                                            }
                                            options={subconditionOptions.ideaFields
                                              ?.filter(
                                                (f) =>
                                                  !!f.type &&
                                                  f.type in conditionChecks,
                                              )
                                              ?.map((f) => ({
                                                key: f.id,
                                                description: f.section,
                                                text: f.title,
                                                value: f.id,
                                              }))}
                                            onChange={(_e, { value }) => {
                                              const fieldId =
                                                dropdownStringValue(value);
                                              const fieldType =
                                                subconditionOptions.ideaFields?.find(
                                                  (f) => f.id === fieldId,
                                                )?.type;
                                              setAnnouncement((prevState) => ({
                                                ...prevState,
                                                sendWhenConditions: {
                                                  ...(prevState.sendWhenConditions ||
                                                    {}),
                                                  conditions:
                                                    prevState.sendWhenConditions?.conditions?.map(
                                                      (c, j) => {
                                                        if (i === j) {
                                                          const defaultCondition =
                                                            isKnownFieldType(
                                                              fieldType,
                                                            )
                                                              ? getDefaultCondition(
                                                                  fieldType,
                                                                )
                                                              : "isNotEmpty";
                                                          return {
                                                            field:
                                                              fieldId ?? null,
                                                            condition:
                                                              defaultCondition ??
                                                              "isNotEmpty",
                                                            value: null,
                                                          } satisfies AnnouncementIdeaFieldCondition;
                                                        }
                                                        return c;
                                                      },
                                                    ),
                                                },
                                              }));
                                            }}
                                          />
                                        </FormField>
                                      </GridColumn>
                                      <GridColumn>
                                        <FormField>
                                          {i === 0 ? (
                                            <FormLabel
                                              htmlFor={`condition-${i}`}
                                            >
                                              2. Meets condition
                                            </FormLabel>
                                          ) : null}
                                          <Dropdown
                                            placeholder="Select a condition"
                                            variant="outline"
                                            value={ideaCondition.condition}
                                            options={
                                              isKnownFieldType(chosenFieldType)
                                                ? availableConditions[
                                                    chosenFieldType
                                                  ]
                                                : undefined
                                            }
                                            onChange={(_e, { value }) => {
                                              const nextCondition =
                                                dropdownStringValue(value);
                                              setAnnouncement((prevState) => ({
                                                ...prevState,
                                                sendWhenConditions: {
                                                  ...(prevState.sendWhenConditions ||
                                                    {}),
                                                  conditions:
                                                    prevState.sendWhenConditions?.conditions?.map(
                                                      (c, j) => {
                                                        if (i === j) {
                                                          return {
                                                            ...(c as AnnouncementIdeaFieldCondition),
                                                            condition:
                                                              nextCondition as AnnouncementIdeaFieldCondition["condition"],
                                                            value: null,
                                                          };
                                                        }
                                                        return c;
                                                      },
                                                    ) as AnnouncementSendWhenCondition[],
                                                },
                                              }));
                                            }}
                                          />
                                        </FormField>
                                      </GridColumn>
                                      <GridColumn>
                                        <FormField>
                                          {i === 0 ? (
                                            <FormLabel
                                              htmlFor={`condition-${i}`}
                                            >
                                              2. Meets condition
                                            </FormLabel>
                                          ) : null}
                                          {ideaCondition.field &&
                                          ideaCondition.condition
                                            ? renderValueCheck(
                                                {
                                                  field: ideaCondition.field,
                                                  condition:
                                                    ideaCondition.condition,
                                                  value:
                                                    ideaCondition.value ??
                                                    undefined,
                                                },
                                                selectedIdeaField,
                                                (value) => {
                                                  setAnnouncement(
                                                    (prevState) => ({
                                                      ...prevState,
                                                      sendWhenConditions: {
                                                        ...(prevState.sendWhenConditions ||
                                                          {}),
                                                        conditions:
                                                          prevState.sendWhenConditions?.conditions?.map(
                                                            (c, j) => {
                                                              if (i === j) {
                                                                return {
                                                                  ...(c as AnnouncementIdeaFieldCondition),
                                                                  value,
                                                                };
                                                              }
                                                              return c;
                                                            },
                                                          ) as AnnouncementSendWhenCondition[],
                                                      },
                                                    }),
                                                  );
                                                },
                                              )
                                            : null}
                                        </FormField>
                                      </GridColumn>
                                      <GridColumn>
                                        <Button
                                          style={{ maxHeight: "fit-content" }}
                                          onClick={() =>
                                            setAnnouncement((prevState) => ({
                                              ...prevState,
                                              sendWhenConditions: {
                                                ...(prevState.sendWhenConditions ||
                                                  {}),
                                                conditions:
                                                  prevState.sendWhenConditions?.conditions?.filter(
                                                    (_c, j) => j !== i,
                                                  ),
                                              },
                                            }))
                                          }
                                        >
                                          <Icon icon={Trash2Icon} />
                                        </Button>
                                      </GridColumn>
                                    </Grid>
                                  </>
                                );
                              },
                            )}
                            <Button
                              onClick={() => {
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  sendWhenConditions: {
                                    ...(prevState.sendWhenConditions || {}),
                                    conditions: [
                                      ...(prevState.sendWhenConditions
                                        ?.conditions ?? []),
                                      {
                                        field: null,
                                        condition: "isNotEmpty",
                                        value: null,
                                      } satisfies AnnouncementIdeaFieldCondition,
                                    ],
                                  },
                                }));
                              }}
                            >
                              Add a condition
                            </Button>
                          </>
                        ) : null}
                        {announcement.sendWhen === "moveToLane" ? (
                          <>
                            <p>Select a lane</p>
                            <Dropdown
                              variant="outline"
                              value={(() => {
                                const firstCondition =
                                  announcement.sendWhenConditions
                                    ?.conditions?.[0];
                                if (!firstCondition) return undefined;
                                if ("laneId" in firstCondition) {
                                  return (
                                    firstCondition.laneId ??
                                    firstCondition.value ??
                                    undefined
                                  );
                                }
                                if ("value" in firstCondition) {
                                  return firstCondition.value ?? undefined;
                                }
                                return undefined;
                              })()}
                              onChange={(_e, { value }) => {
                                const laneId = dropdownStringValue(value);
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  sendWhenConditions: {
                                    ...(prevState.sendWhenConditions || {}),
                                    conditions: [{ laneId, value: laneId }],
                                  },
                                }));
                              }}
                              options={mapSubconditionOptions(
                                subconditionOptions.moveToLane,
                              )}
                              placeholder="Select a lane"
                            />
                          </>
                        ) : null}
                        {announcement.sendWhen ===
                        "submittedAllAssessmentsForChallenge" ? (
                          <Dropdown
                            variant="outline"
                            value={(() => {
                              const firstCondition =
                                announcement.sendWhenConditions
                                  ?.conditions?.[0];
                              if (
                                firstCondition &&
                                "assessmentRoundId" in firstCondition
                              ) {
                                return (
                                  firstCondition.assessmentRoundId ?? undefined
                                );
                              }
                              return undefined;
                            })()}
                            onChange={(_e, { value }) => {
                              const assessmentRoundId =
                                dropdownStringValue(value);
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                sendWhenConditions: {
                                  ...(prevState.sendWhenConditions || {}),
                                  conditions: [{ assessmentRoundId }],
                                },
                              }));
                            }}
                            options={mapSubconditionOptions(
                              subconditionOptions.assessmentRounds,
                            )}
                            placeholder="Select an assessment"
                          />
                        ) : null}
                      </>
                    </div>
                  )}
                </div>
              )}
          </CardContent>
        </Card>
        {announcement.countSent ? (
          <p>
            This announcement has been sent {announcement.countSent || 0} times.
          </p>
        ) : null}
        <AnnouncementBase
          forType={forType}
          forId={forId}
          announcement={announcement}
          setAnnouncement={setAnnouncement}
        />
        {announcement.type !== "automatic" && (
          <Message color="blue" size="small">
            <Icon icon={InfoIcon} />
            <MessageHeader>
              Don&apos;t use {util.appName()} to send out marketing messages
            </MessageHeader>
            <MessageContent>
              For GDPR purposes, announcements should be used only for platform
              activity and not for advertising events, services or other
              marketing correspondence.
            </MessageContent>
          </Message>
        )}
      </ModalContent>

      <ModalActions>
        {announcement.type !== "automatic" && (
          <span>
            {t("announcements.form.info", { audience: recipientsMatched })}
          </span>
        )}
        <Button
          style={{ marginLeft: 16 }}
          onClick={() => setAnnouncement(EMPTY_ANNOUNCEMENT)}
        >
          {t("generic.cancel")}
        </Button>
        {announcement._id ? (
          <Button
            onClick={updateAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            tooltip={sendDisabledReason}
            color="secondary"
          >
            <Icon icon={SaveIcon} />
            {t("generic.saveChanges")}
          </Button>
        ) : announcement.type !== "automatic" && announcement.sendAt ? (
          <Button
            onClick={createAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            tooltip={sendDisabledReason}
            color="secondary"
          >
            <Icon icon={CalendarIcon} />
            {sendAtValid
              ? `Send on ${formatDateAt(announcement.sendAt)}`
              : "Invalid date entered"}
          </Button>
        ) : (
          <Button
            onClick={createAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            style={{ marginLeft: "0" }}
            tooltip={sendDisabledReason}
            color="secondary"
          >
            <Icon
              icon={announcement.type === "standard" ? SendIcon : PlusIcon}
            />
            {announcement.type === "standard"
              ? t("announcements.form.send")
              : "Create announcement"}
          </Button>
        )}
      </ModalActions>
      <ModalActions>
        <Button
          tooltip={sendDisabledReason}
          size="small"
          onClick={() => setEmailPreviewOpen(true)}
          disabled={sendDisabled}
          variant="outline"
        >
          <Icon icon={EyeIcon} />
          Preview email
        </Button>
        {!announcement._id && (
          <Button
            size="small"
            onClick={() => {
              setAnnouncement(EMPTY_ANNOUNCEMENT);
              removeLocalStorageItem(`announcement-${forId}`);
            }}
            variant="outline"
          >
            <Icon icon={Trash2Icon} />
            {t("generic.delete")}
          </Button>
        )}
        {announcement._id ? (
          <>
            {copyToNew ? (
              <Button
                size="small"
                onClick={() => {
                  setAnnouncement(EMPTY_ANNOUNCEMENT);
                  window.scrollTo(0, 0);
                  setTimeout(() => {
                    copyToNew(announcement);
                  }, 300);
                }}
                variant="outline"
              >
                <Icon icon={CopyIcon} />
                Copy to new
              </Button>
            ) : null}
            <Button
              onClick={() => {
                deleteAnnouncement(announcement._id ?? "", () => {
                  setAnnouncement(EMPTY_ANNOUNCEMENT);
                });
              }}
              size="small"
              variant="outline"
            >
              <Icon icon={Trash2Icon} />
              {t("generic.delete")}
            </Button>
          </>
        ) : null}
      </ModalActions>
    </Modal>
  );
};

export default AnnouncementModal;
